import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import { getVerificationInformationSumsubResult, type VerificationInformationSumsubResultGetResponse } from '@/services/api/profile';

const POLL_INTERVAL_MS = 5_000;
const MAX_POLL_COUNT = 10;

const profileVerificationSumsubResultQueryKey = (pid: UserPid) => ['profile', 'verification-information-sumsub-result', pid];

const profileVerificationSumsubResultQueryOptions = (pid: UserPid, enablePolling = false) =>
  queryOptions({
    enabled: !!pid,
    refetchInterval: enablePolling ? POLL_INTERVAL_MS : false,
    queryKey: profileVerificationSumsubResultQueryKey(pid),
    queryFn: getVerificationInformationSumsubResult,
  });

const useProfileVerificationSumsubResult = (enablePolling = false) => {
  const { pid } = useUserPid();
  const queryClient = useQueryClient();
  const pollCount = useRef(0);
  const shouldPoll = enablePolling && pollCount.current < MAX_POLL_COUNT;
  const queryResult = useQuery(profileVerificationSumsubResultQueryOptions(pid, shouldPoll));

  useEffect(() => {
    if (!enablePolling) pollCount.current = 0;
  }, [enablePolling]);

  const set = useCallback(
    (data: Partial<VerificationInformationSumsubResultGetResponse>) => {
      queryClient.setQueryData(profileVerificationSumsubResultQueryKey(pid), (previousData: VerificationInformationSumsubResultGetResponse) => ({
        ...previousData,
        ...data,
      }));
    },
    [pid, queryClient],
  );

  return {
    ...queryResult,
    set,
  };
};

export { useProfileVerificationSumsubResult };
